import { FC } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import {
  SellPlan,
  SellSelectionTotals,
} from 'modules/incentivesMarket/pages/IncentiveOfferDetail/types/SellUnitsSelection';
import { planNet } from 'modules/incentivesMarket/pages/IncentiveOfferDetail/utils/sellUnitsCalculations';

type SellInvoiceTableProps = {
  plans: SellPlan[];
  selected: Record<string, number>;
  totals: SellSelectionTotals;
  hasUnits: boolean;
};

type LineProps = {
  label: string;
  value: string;
  muted?: boolean;
};

const Line: FC<LineProps> = ({ label, value, muted }) => (
  <div className="d-flex justify-content-between">
    <span className={muted ? 'text-soft' : 'text-secondary'}>{label}</span>
    <span
      className={`tnum${muted ? ' text-soft' : ' fw-medium text-secondary'}`}
    >
      {value}
    </span>
  </div>
);

const SellInvoiceTable: FC<SellInvoiceTableProps> = ({
  plans,
  selected,
  totals,
  hasUnits,
}) => {
  const { t } = useTranslate();

  const activePlans = plans.filter((plan) => selected[plan.id] > 0);
  const showRetention = plans.some((plan) => plan.retention > 0);
  const columnCount = showRetention ? 6 : 5;

  return (
    <div className="incentive-offer-sell-units-panel__invoice">
      <div className="table-responsive">
        <table className="incentive-offer-sell-units-panel__invoice-table">
          <thead>
            <tr>
              <th className="text-left">{t('Plan')}</th>
              <th className="text-right">{t('IncentivesMarketUnits')}</th>
              <th className="text-right">{t('StrikePriceUnit')}</th>
              <th className="text-right">{t('SellingPriceUnit')}</th>
              {showRetention && (
                <th className="text-right">{t('PercentRetentionColumn')}</th>
              )}
              <th className="text-right">{t('Profit')}</th>
            </tr>
          </thead>
          <tbody className="tnum">
            {!hasUnits && (
              <tr>
                <td
                  colSpan={columnCount}
                  className="incentive-offer-sell-units-panel__invoice-empty"
                >
                  <p className="mb-1 fs-14px fw-medium text-soft">
                    {t('IncentivesMarketSellEmptyBreakdown')}
                  </p>
                  <p className="mb-0 fs-12px text-soft">
                    {t('IncentivesMarketSellEmptyBreakdownHint')}
                  </p>
                </td>
              </tr>
            )}
            {hasUnits &&
              activePlans.map((plan) => {
                const units = selected[plan.id];
                return (
                  <tr key={plan.id} className="row-in">
                    <td className="text-left">
                      <div className="fw-medium text-dark">{plan.name}</div>
                      {plan.type && (
                        <div className="fs-11px text-soft">{plan.type}</div>
                      )}
                    </td>
                    <td className="text-right fw-medium text-secondary">
                      {formatNumber(units)}
                      <small className="text-soft">/{plan.maxSellable}</small>
                    </td>
                    <td className="text-right text-soft">
                      {formatCurrencyDecimals(plan.exercisePrice)}
                    </td>
                    <td className="text-right text-soft">
                      {formatCurrencyDecimals(plan.salePrice)}
                    </td>
                    {showRetention && (
                      <td className="text-right text-soft">
                        {formatNumber(plan.retention)}%
                      </td>
                    )}
                    <td className="text-right fw-semibold text-dark">
                      {formatCurrencyDecimals(planNet(plan, units))}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {hasUnits && (
        <div className="incentive-offer-sell-units-panel__invoice-breakdown tnum">
          <Line
            label={t('IncentivesMarketSellSaleValue')}
            value={formatCurrencyDecimals(totals.gross)}
          />
          <Line
            label={t('IncentivesMarketSellExerciseCost')}
            value={`− ${formatCurrencyDecimals(totals.cost)}`}
            muted
          />
          {showRetention && (
            <Line
              label={t('IncentivesMarketSellTaxRetention')}
              value={`− ${formatCurrencyDecimals(totals.retention)}`}
              muted
            />
          )}
        </div>
      )}
    </div>
  );
};

export default SellInvoiceTable;
